import React, { Dispatch, SetStateAction } from "react"
import { getCountryName } from "helpers/countries"
import dayjs from "dayjs"
import DineroFactory from "dinero.js"
import {
  CustomReasonCode,
  Order,
  OrderModificationShippingFees,
  Reason,
  ShippingFee
} from "lib/types/generated/graphql-types"
import { useQuery } from "@apollo/client"

import GET_STORE_GROUP from "../../../../../../graphql/queries/store/GetStoreGroup"

import { Header } from "../../Shared.styled"
import { Blur, Container, Summary } from "./OrderSummary.styled"

import Money from "../../../../../Money"
import { Item, Label, Value } from "../../../../../Ui/List/List"
import FlagIcon from "../../../../../Ui/FlagIcon"
import DotLoader from "../../../../../Ui/DotLoader"
import CopyButton from "../../../../../Ui/CopyButton"
import { ShippingFeeRow } from "./ShippingFeeRow"
import { ModificationShippingFees } from "../../../../../../classes/order/ModificationShippingFees"
import ReasonInput from "../../../Reason/ReasonInput"
import { ReasonType } from "lib/types/order"

type Props = {
  order: Order
  refetch: () => void
  orderLoading: boolean
  editShippingFee: ShippingFee | undefined
  setEditShippingFee: Dispatch<SetStateAction<ShippingFee | undefined>>
  reason: Reason
  setReason: Dispatch<SetStateAction<Reason>>
  reasonType: ReasonType | undefined
  setReasonType: Dispatch<SetStateAction<ReasonType | undefined>>
  customReasons: CustomReasonCode[]
}

export const OrderSummary = ({
  order,
  refetch,
  setEditShippingFee,
  orderLoading,
  reason,
  setReason,
  reasonType,
  setReasonType,
  customReasons
}: Props) => {
  const modifications = new ModificationShippingFees(
    order.Modifications as OrderModificationShippingFees[]
  )

  const { data, loading } = useQuery(GET_STORE_GROUP, {
    fetchPolicy: "cache-first",
    variables: { id: order?.storeGroupId }
  })

  if (!order)
    return (
      <Blur>
        <Header>
          <h2>Order: #1234567</h2>
        </Header>
        <Summary>
          <Item flexibleHeight>
            <Label>E-mail:</Label>
            <Value>
              <>
                john.doe@email.com
                <CopyButton string={""} />
              </>
            </Value>
          </Item>
          <Item>
            <Label>Store group:</Label>
            <Value>store group</Value>
          </Item>
          <Item>
            <Label>Store market:</Label>
            <Value flex>
              <>Sweden</>
            </Value>
          </Item>
          <Item>
            <Label>Order date:</Label>
            <Value>2000-00-00 00:00</Value>
          </Item>
          <Item>
            <Label>Total order value:</Label>
            <Value>SEK 200.00</Value>
          </Item>
        </Summary>
      </Blur>
    )
  if (!order) return null
  return (
    <Container>
      <Header>
        <h2>Order: #{order?.reference}</h2>
      </Header>
      <Summary>
        <Item flexibleHeight>
          <Label>E-mail:</Label>
          <Value>
            <>
              {order?.billingAddress?.email}
              <CopyButton string={order?.billingAddress?.email} />
            </>
          </Value>
        </Item>
        <Item>
          <Label>Store group:</Label>
          <Value>{loading ? <DotLoader /> : data?.getStoreGroup?.name}</Value>
        </Item>
        <Item>
          <Label>Store market:</Label>
          <Value flex>
            <>
              <FlagIcon countryCode={order?.countryCode} />
              {getCountryName(order?.countryCode)}
            </>
          </Value>
        </Item>
        <Item>
          <Label>Order date:</Label>
          <Value>{dayjs(order?.date).format("YYYY-MM-DD HH:mm")}</Value>
        </Item>
        {modifications.getPendingModification()?.oldTotals?.grandTotal ? (
          <Item>
            <Label>Original order value:</Label>
            <Value>
              <Money
                amount={parseInt(
                  `${modifications.getPendingModification()?.oldTotals?.grandTotal}`
                )}
                currencyUnit={order?.currencyCode as DineroFactory.Currency}
              />
            </Value>
          </Item>
        ) : (
          <></>
        )}
        <Item>
          <Label>Total order value:</Label>
          <Value>
            <Money
              amount={parseInt(`${order?.totals?.grandTotal ?? 0}`)}
              currencyUnit={order?.currencyCode as DineroFactory.Currency}
            />
          </Value>
        </Item>

        {!modifications.getPendingModification() && (
          <>
            <Label>Reason:</Label>
            <ReasonInput
              setReason={setReason}
              reason={reason}
              customReasons={customReasons}
              reasonType={reasonType}
              setReasonType={setReasonType}
            />
          </>
        )}
      </Summary>
      {((reason.cause && reason.code) || reasonType === ReasonType.SKIP) && (
        <>
          <h2>Shipping Fees ({order.shippingFees?.length ?? 0})</h2>
          {order.shippingFees?.map((shippingFee) => (
            <ShippingFeeRow
              key={shippingFee.id}
              order={order}
              orderLoading={orderLoading}
              shippingFee={shippingFee}
              setEditShippingFee={setEditShippingFee}
              refetch={refetch}
            />
          ))}
        </>
      )}
    </Container>
  )
}
